import { Outlet, Link, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Instagram, Phone, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import Logo from "@/components/Logo";
import { useLanguage } from "@/context/LanguageContext";
import { blogCommon } from "@/i18n/blogPosts";

export function LanguageToggle({ className = "", testId = "language-toggle" }) {
  const { lang, setLang } = useLanguage();
  return (
    <div
      data-testid={testId}
      className={`inline-flex items-center gap-0.5 p-1 rounded-full bg-slate-100 border border-slate-200 ${className}`}
    >
      {["fr", "en"].map((l) => (
        <button
          key={l}
          data-testid={`${testId}-${l}`}
          onClick={() => setLang(l)}
          aria-pressed={lang === l}
          className={`px-2.5 py-1 rounded-full text-xs font-bold uppercase tracking-wide transition-colors ${
            lang === l ? "bg-slate-900 text-white shadow-sm" : "text-slate-500 hover:text-slate-900"
          }`}
        >
          {l}
        </button>
      ))}
    </div>
  );
}

export default function Layout() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();
  const { t, lang } = useLanguage();

  const links = [
    { to: "/", label: t("nav.home"), testId: "menu-nav-home" },
    { to: "/pellicules", label: t("nav.films"), testId: "menu-nav-films" },
    { to: "/pellicules/miroir", label: t("films.miroirCard.label"), testId: "menu-nav-miroir", sub: true },
    { to: "/pellicules/givre", label: t("films.givreCard.label"), testId: "menu-nav-givre", sub: true },
    { to: "/pellicules/securite", label: t("films.securiteCard.label"), testId: "menu-nav-securite", sub: true },
    { to: "/services", label: t("nav.services"), testId: "menu-nav-services" },
    { to: "/a-propos", label: t("nav.about"), testId: "menu-nav-about" },
    { to: "/contact", label: t("nav.contact"), testId: "menu-nav-contact" },
    { to: "/grossiste", label: t("wholesale.nav"), testId: "menu-nav-wholesale" },
    { to: "/conseils", label: blogCommon[lang].eyebrow, testId: "menu-nav-blog" },
  ];

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 12);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
    window.scrollTo({ top: 0, behavior: "instant" });
  }, [pathname]);

  // Verrouille le défilement + fermeture par Échap quand le menu est ouvert
  useEffect(() => {
    document.body.style.overflow = open ? "hidden" : "";
    const onKey = (e) => e.key === "Escape" && setOpen(false);
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = "";
      window.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <>
      <header
        data-testid="site-header"
        className={`fixed inset-x-0 top-0 z-50 transition-all duration-300 backdrop-blur-xl bg-white/95 border-b border-slate-200/60 ${
          scrolled ? "shadow-[0_4px_30px_rgba(0,0,0,0.04)]" : ""
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 lg:px-8 flex items-center justify-between h-20">
          <Link to="/" data-testid="brand-logo" className="flex items-center group">
            <Logo />
          </Link>

          <div className="flex items-center gap-3">
            <LanguageToggle testId="language-toggle-header" />
            <button
              data-testid="menu-toggle"
              onClick={() => setOpen(true)}
              aria-label={t("menu.title")}
              aria-expanded={open}
              className="p-2.5 rounded-xl hover:bg-slate-100 transition-colors"
            >
              <Menu className="w-6 h-6" />
            </button>
          </div>
        </div>
      </header>

      {/* MENU, panneau latéral (plein écran sur mobile) */}
      <div
        data-testid="menu-overlay"
        aria-hidden={!open}
        className={`fixed inset-0 z-[70] transition-opacity duration-300 ${
          open ? "opacity-100" : "opacity-0 pointer-events-none"
        }`}
      >
        <div
          data-testid="menu-backdrop"
          onClick={() => setOpen(false)}
          className="absolute inset-0 bg-slate-950/60 backdrop-blur-sm"
        />
        <aside
          className={`absolute right-0 top-0 h-full w-full sm:max-w-md bg-[#0F1012] text-white flex flex-col overflow-y-auto transition-transform duration-300 ease-out ${
            open ? "translate-x-0" : "translate-x-full"
          }`}
        >
          <div className="flex items-center justify-between px-6 h-20 border-b border-white/10 flex-shrink-0">
            <span className="text-xs tracking-[0.3em] uppercase font-display font-semibold text-[#C5A880]">
              {t("menu.title")}
            </span>
            <button
              data-testid="menu-close"
              onClick={() => setOpen(false)}
              aria-label={t("menu.close")}
              className="p-2.5 rounded-xl hover:bg-white/10 transition-colors"
            >
              <X className="w-6 h-6" />
            </button>
          </div>

          <nav className="flex-1 px-6 py-8 flex flex-col gap-1">
            {links.map((l, i) => (
              <Link
                key={l.to}
                to={l.to}
                data-testid={l.testId}
                style={{ transitionDelay: open ? `${60 + i * 40}ms` : "0ms" }}
                className={
                  l.sub
                    ? `font-heading text-lg sm:text-xl font-normal py-2.5 pl-6 border-b border-white/5 hover:text-[#C5A880] transition-all duration-300 ${
                        pathname === l.to ? "text-[#C5A880]" : "text-white/60"
                      } ${open ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}`
                    : `font-heading text-2xl sm:text-3xl font-normal py-3 border-b border-white/5 hover:text-[#C5A880] hover:pl-2 transition-all duration-300 ${
                        pathname === l.to ? "text-[#C5A880]" : "text-white"
                      } ${open ? "opacity-100 translate-x-0" : "opacity-0 translate-x-4"}`
                }
              >
                {l.label}
              </Link>
            ))}
          </nav>

          <div className="px-6 pb-10 flex-shrink-0">
            <Link to="/contact" data-testid="menu-cta-estimate" onClick={() => setOpen(false)}>
              <Button className="w-full rounded-full bg-[#8C6D4F] hover:bg-[#6E5639] text-white h-14 text-base font-medium shadow-lg shadow-black/20">
                {t("nav.cta")}
              </Button>
            </Link>
            <div className="mt-6 flex items-center justify-between text-sm text-white/70">
              <a
                href="tel:+14382556438"
                data-testid="menu-phone-link"
                className="inline-flex items-center gap-2 hover:text-[#C5A880] transition-colors"
              >
                <Phone className="w-4 h-4" /> 438 255 6438
              </a>
              <a
                href="https://www.instagram.com/reflectum.pellicule/"
                target="_blank"
                rel="noopener noreferrer"
                data-testid="menu-instagram-link"
                className="inline-flex items-center gap-2 hover:text-[#C5A880] transition-colors"
              >
                <Instagram className="w-4 h-4" /> @reflectum.pellicule
              </a>
            </div>
          </div>
        </aside>
      </div>

      <main className="pt-20">
        <Outlet />
      </main>

      <Footer />
    </>
  );
}

function Footer() {
  const { t } = useLanguage();
  return (
    <footer data-testid="site-footer" className="border-t border-slate-200 bg-slate-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8 py-14 flex flex-col items-center text-center gap-6">
        <Logo />
        <p className="text-slate-600 max-w-md leading-relaxed">{t("footer.tagline")}</p>
        <div className="flex flex-wrap items-center justify-center gap-x-8 gap-y-3 text-slate-700">
          <a
            href="tel:+14382556438"
            data-testid="footer-phone-link"
            className="inline-flex items-center gap-2 font-medium hover:text-blue-600 transition-colors"
          >
            <Phone className="w-4 h-4" /> 438 255 6438
          </a>
          <a
            href="mailto:contact@reflectumpellicule.com"
            data-testid="footer-email-link"
            className="inline-flex items-center gap-2 font-medium hover:text-blue-600 transition-colors"
          >
            <Mail className="w-4 h-4" /> contact@reflectumpellicule.com
          </a>
          <Link
            to="/contact"
            data-testid="footer-contact-link"
            className="font-medium hover:text-blue-600 transition-colors"
          >
            {t("footer.contactLink")}
          </Link>
          <a
            href="https://www.instagram.com/reflectum.pellicule/"
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-instagram-link"
            className="inline-flex items-center gap-2 font-medium hover:text-blue-600 transition-colors"
          >
            <Instagram className="w-4 h-4" /> @reflectum.pellicule
          </a>
        </div>
        <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-sm text-slate-500">
          <Link to="/pellicules/miroir" data-testid="footer-miroir-link" className="hover:text-blue-600 transition-colors">
            {t("films.miroirCard.label")}
          </Link>
          <Link to="/pellicules/givre" data-testid="footer-givre-link" className="hover:text-blue-600 transition-colors">
            {t("films.givreCard.label")}
          </Link>
          <Link to="/pellicules/securite" data-testid="footer-securite-link" className="hover:text-blue-600 transition-colors">
            {t("films.securiteCard.label")}
          </Link>
        </div>
        <p data-testid="footer-areas" className="text-sm text-slate-500">{t("footer.areas")}</p>
        <p className="text-sm text-slate-500">
          {t("footer.wholesaleText")}{" "}
          <Link
            to="/grossiste"
            data-testid="footer-wholesale-link"
            className="font-medium text-slate-600 underline underline-offset-4 hover:text-blue-600 transition-colors"
          >
            {t("footer.wholesaleLink")}
          </Link>
        </p>
      </div>
      <div className="border-t border-slate-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-8 pt-6 pb-24 sm:py-6 text-sm text-slate-500 flex flex-col md:flex-row justify-between gap-2">
          <div>© {new Date().getFullYear()} {t("footer.rights")}</div>
          <div className="flex items-center gap-4">
            <Link to="/confidentialite" data-testid="footer-privacy-link" className="hover:text-blue-600 transition-colors">
              {t("footer.privacy")}
            </Link>
            <span className="text-slate-300">·</span>
            <span>{t("footer.badge")}</span>
          </div>
        </div>
      </div>
    </footer>
  );
}
